
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface Product {
    id_product: string;
    type: string;
    product_name: string;
    description: string;
    id_providers: string;
    isactive?: boolean;

}

type ProductInput = Omit<Product, 'id_product'>

type ProductUpdateInput = Omit<Product, 'id_providers'>

interface ProductsProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface ProductsContextData {
    createProduct: (product: ProductInput) => Promise<ResponseProps>;
    updateProduct: (product: ProductUpdateInput) => Promise<ResponseProps>;
    updateOnDelete: (id: string | number) => Promise<ResponseProps>;
}

interface UseProductsOptions {
    loadProducts?: boolean;
}

interface UseProductsData extends ProductsContextData {
    products: Product[];
}

const ProductsContext = createContext<ProductsContextData>(
    {} as ProductsContextData
);

const productsQueryKey = ['products'];
const emptyProducts: Product[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchProducts() {
  const response = await apiV2.get('products');
  return Array.isArray(response?.data) ? response.data : [];
}

export function ProductsProvider({ children }: ProductsProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateProducts() {
    queryClient.invalidateQueries({ queryKey: productsQueryKey });
  }

  async function updateOnDelete(id: string | number) {
    const response = await apiV2.delete(`/products/${id}`);
    if (response.status === 200) {
      invalidateProducts();
      return {
        success: true,
        message: "Removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover."
    };
  }

  async function createProduct(productInput: ProductInput) {
    const productInfo = {
      type: productInput.type,
      product_name: productInput.product_name,
      description: productInput.description
    };
    const response = await apiV2.post('/products', productInfo);
    if (response.status === 201) {
      invalidateProducts();
      return {
        success: true,
        message: "Adicionado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar."
    };

  }

  async function updateProduct(product: ProductUpdateInput) {
    const response = await apiV2.put(`/products/${product.id_product}`, product);
    if (response.status === 204) {
      invalidateProducts();
      return {
        success: true,
        message: "Editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar."
    };
  }

  return (
    <ProductsContext.Provider value={{ createProduct, updateProduct, updateOnDelete }}>
      {children}
    </ProductsContext.Provider>
  );
}

export function UseProducts(options: UseProductsOptions = {}): UseProductsData {
  const context = useContext(ProductsContext);
  const { loadProducts = false } = options;

  const productsQuery = useQuery({
    queryKey: productsQueryKey,
    queryFn: fetchProducts,
    enabled: loadProducts && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    products: productsQuery.data || emptyProducts
  };
}
